import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { getApiUrl } from '../config/api';
import PageMeta from '../components/PageMeta';

const PAYWALL_PLANS = {
  BASIC: {
    code: 'basic',
    badge: 'Basic Intelligence',
    title: 'Unlock Deeper Analysis',
    description: 'You are seeing partial content. Unlock the complete segment drill-down, balance sheet analysis, and institutional monitor metrics with a Basic subscription.',
    cta: 'Unlock Basic &#8594; &#8377;299/month',
    note: 'No lock-in · Cancel anytime · Instant access',
    features: [
      { icon: '&#128202;', label: 'Full Segment Financials (Q1-Q3 FY26)' },
      { icon: '&#128200;', label: 'Quarterly Trend Analysis' },
      { icon: '&#128176;', label: 'Balance Sheet Deep-Dive' },
      { icon: '&#128204;', label: '8 Key Monitor Metrics' },
    ],
  },
  PREMIUM: {
    code: 'premium',
    badge: 'Premium Intelligence',
    title: 'Unlock Full Institutional Analysis',
    description: 'The complete analytical framework institutional investors use - SOTP valuation table, DCF with WACC, FCF bridge, scenarios, and positioning framework.',
    cta: 'Unlock Premium &#8594; &#8377;999/month',
    note: 'Investor-grade models · Premium thesis · Priority access',
    features: [
      { icon: '&#128208;', label: 'Full SOTP - All 6 Segments Valued' },
      { icon: '&#128202;', label: 'DCF - Jio Platforms (5-Year Explicit)' },
      { icon: '&#9881;', label: 'WACC Derivation + Cost of Equity' },
      { icon: '&#127919;', label: '3-Scenario Analysis with Probabilities' },
      { icon: '&#128506;', label: '4-Profile Positioning Framework' },
      { icon: '&#128269;', label: '"What the Market Is Missing" Insight' },
      { icon: '&#128203;', label: 'Final Long-Term Analyst Thesis' },
    ],
  },
};

const getPaywallPlan = (requiredAccess) => {
  const access = String(requiredAccess || '').toUpperCase();
  return PAYWALL_PLANS[access] || PAYWALL_PLANS.BASIC;
};

const getPaywallHref = (plan, isAuthenticated) => {
  const subscriptionPath = `/subscription?plan=${plan.code}&billing=monthly`;
  return isAuthenticated
    ? subscriptionPath
    : `/signin?redirect=${encodeURIComponent(subscriptionPath)}`;
};

const SubscriptionReportViewer = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [iframeHeight, setIframeHeight] = useState(900);

  const syncIframeHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) {
        return;
      }

      const body = doc.body;
      const html = doc.documentElement;
      const nextHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0,
        720
      );

      setIframeHeight(Math.ceil(nextHeight) + 4);
    } catch (e) {
      console.warn('Unable to resize report iframe:', e);
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    syncIframeHeight();
    window.setTimeout(syncIframeHeight, 120);
    window.setTimeout(syncIframeHeight, 450);
    window.setTimeout(syncIframeHeight, 1200);

    try {
      const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
      const fontsReady = doc?.fonts?.ready;
      if (fontsReady?.then) {
        fontsReady.then(syncIframeHeight).catch(() => {});
      }
    } catch (e) {
      // Font readiness is a progressive enhancement for same-origin iframe documents.
    }
  }, [syncIframeHeight]);

  useEffect(() => {
    window.addEventListener('resize', syncIframeHeight);
    return () => window.removeEventListener('resize', syncIframeHeight);
  }, [syncIframeHeight]);

  useEffect(() => {
    let cancelled = false;

    const checkAuthentication = async () => {
      try {
        const API = getApiUrl();
        const accessToken = localStorage.getItem('access_token');
        const resp = await fetch(`${API}/auth/me`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          credentials: 'include',
        });

        if (!cancelled) {
          setIsAuthenticated(resp.ok);
        }
      } catch (e) {
        console.error('Unable to check report viewer authentication:', e);
        if (!cancelled) {
          setIsAuthenticated(false);
        }
      }
    };

    if (reportId) {
      checkAuthentication();
    }

    return () => {
      cancelled = true;
    };
  }, [reportId]);

  useEffect(() => {
    const fetchPage = async () => {
      if (!reportId) {
        return;
      }

      try {
        setLoading(true);
        setError('');
        const API = getApiUrl();
        // Load all pages in one request so the report can be displayed as a single scrollable document.
        const resp = await fetch(`${API}/subscription-reports/${reportId}?page=0&size=10000`, {
          method: 'GET',
          credentials: 'include',
        });

        if (!resp.ok) {
          setError(`Failed to load report (Status: ${resp.status})`);
          setReport(null);
          return;
        }

        const data = await resp.json();
        setReport(data);
      } catch (e) {
        console.error('Failed to fetch report:', e);
        setError('Failed to load report');
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [reportId]);

  const totalPages = report?.totalPages || report?.total_pages || 0;
  const pages = report?.pages || [];

  useEffect(() => {
    setIframeHeight(900);
  }, [report]);

  const iframeDoc = useMemo(() => {
    const originalHtml = report?.originalHtml || report?.original_html;
    if (!originalHtml) {
      return null;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(originalHtml, 'text/html');

      const paywallCss = `
        html, body { overflow: hidden !important; }
        body { margin: 0 !important; }
        .container {
          max-width: min(1180px, calc(100vw - 40px)) !important;
          padding-left: 24px !important;
          padding-right: 24px !important;
        }
        .site-header, .hero { padding-left: 36px !important; padding-right: 36px !important; }
        .btree-paywall-wrap {
          position: relative;
          min-height: 560px;
          margin: 44px 0 52px;
          border: 1px solid rgba(198, 178, 126, 0.24);
          border-radius: 22px;
          overflow: hidden;
          background: #f8f6f1;
          box-shadow: 0 28px 80px rgba(28, 43, 74, 0.12), inset 0 1px 0 rgba(255,255,255,0.82);
          isolation: isolate;
        }
        .btree-paywall-blur {
          position: absolute;
          inset: -18px;
          filter: blur(7px);
          opacity: 0.34;
          pointer-events: none;
          user-select: none;
          transform: scale(1.018);
        }
        .btree-paywall-blur .section { margin: 0 !important; }
        .btree-paywall-overlay {
          position: relative;
          z-index: 2;
          min-height: 560px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg, rgba(248,246,241,0.68) 0%, rgba(248,246,241,0.94) 48%, rgba(248,246,241,0.82) 100%);
          padding: 56px 28px;
          text-align: center;
          -webkit-backdrop-filter: blur(2px) saturate(1.08);
          backdrop-filter: blur(2px) saturate(1.08);
        }
        .btree-paywall-panel {
          width: 100%;
          max-width: 1080px;
          margin: 0 auto;
          color: #061b3f;
        }
        .btree-paywall-lock {
          display: inline-flex;
          width: 44px;
          height: 44px;
          align-items: center;
          justify-content: center;
          margin-bottom: 22px;
          border-radius: 10px;
          background: linear-gradient(135deg, #ffd45d 0%, #f49a39 48%, #e9653e 100%);
          box-shadow: 0 14px 34px rgba(238, 119, 45, 0.32), inset 0 1px 0 rgba(255,255,255,0.72);
          font-size: 24px;
        }
        .btree-paywall-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          padding: 10px 22px;
          border: 1px solid rgba(20, 42, 79, 0.22);
          border-radius: 999px;
          background: linear-gradient(180deg, rgba(255,255,255,0.86), rgba(229,233,239,0.80));
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.92), 0 12px 28px rgba(28,43,74,0.10);
          color: #061b3f;
          font: 700 13px/1.2 Arial, sans-serif;
          letter-spacing: 3px;
          text-transform: uppercase;
        }
        .btree-paywall-basic .btree-paywall-badge { display: none; }
        .btree-paywall-premium .btree-paywall-lock { display: none; }
        .btree-paywall-title {
          margin: 0 auto 20px;
          max-width: 900px;
          color: #061b3f;
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(34px, 4vw, 50px);
          font-weight: 500;
          line-height: 1.08;
          letter-spacing: 0;
        }
        .btree-paywall-copy {
          max-width: 790px;
          margin: 0 auto;
          color: rgba(17, 35, 62, 0.80);
          font: 400 20px/1.7 Arial, sans-serif;
        }
        .btree-paywall-chips {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 14px;
          margin: 34px auto 32px;
        }
        .btree-paywall-chip {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          min-height: 46px;
          padding: 10px 18px;
          border: 1px solid rgba(66, 133, 244, 0.34);
          border-radius: 999px;
          background: linear-gradient(180deg, rgba(238,246,255,0.92), rgba(225,237,255,0.72));
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.90), 0 10px 28px rgba(35, 95, 169, 0.10);
          color: #123e82;
          font: 500 16px/1.35 Arial, sans-serif;
          -webkit-backdrop-filter: blur(12px);
          backdrop-filter: blur(12px);
        }
        .btree-paywall-premium .btree-paywall-chip {
          border-color: rgba(20, 42, 79, 0.20);
          background: linear-gradient(180deg, rgba(255,255,255,0.88), rgba(229,232,236,0.78));
          color: #061b3f;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.92), 0 10px 26px rgba(20, 42, 79, 0.10);
        }
        .btree-paywall-chip-icon { opacity: 0.86; }
        .btree-paywall-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: min(440px, 100%);
          min-height: 74px;
          padding: 18px 34px;
          border-radius: 11px;
          background: linear-gradient(135deg, #2e60a5 0%, #214f92 50%, #163e7a 100%);
          box-shadow: 0 22px 44px rgba(31, 78, 146, 0.26), inset 0 1px 0 rgba(255,255,255,0.18);
          color: #ffffff !important;
          font: 800 24px/1.1 Arial, sans-serif;
          text-decoration: none;
        }
        .btree-paywall-premium .btree-paywall-cta {
          background: linear-gradient(135deg, #203765 0%, #172a4d 55%, #0f1f3b 100%);
          box-shadow: 0 24px 50px rgba(15, 31, 59, 0.30), inset 0 1px 0 rgba(255,255,255,0.14);
        }
        .btree-paywall-cta:hover { transform: translateY(-1px); }
        .btree-paywall-note {
          margin-top: 18px;
          color: rgba(17, 35, 62, 0.56);
          font: 400 15px/1.5 Arial, sans-serif;
        }
        @media (max-width: 768px) {
          .container {
            max-width: 100% !important;
            padding-left: 14px !important;
            padding-right: 14px !important;
          }
          .site-header, .hero { padding-left: 18px !important; padding-right: 18px !important; }
          .btree-paywall-wrap { min-height: 620px; margin: 32px 0 40px; border-radius: 16px; }
          .btree-paywall-overlay { min-height: 620px; padding: 42px 16px; }
          .btree-paywall-title { font-size: 33px; }
          .btree-paywall-copy { font-size: 16px; line-height: 1.65; }
          .btree-paywall-chip {
            width: 100%;
            justify-content: center;
            font-size: 14px;
            min-height: 42px;
          }
          .btree-paywall-cta {
            min-height: 64px;
            font-size: 19px;
            padding-left: 20px;
            padding-right: 20px;
          }
        }
      `;

      const styleEl = doc.createElement('style');
      styleEl.textContent = paywallCss;
      doc.head.appendChild(styleEl);

      Array.from(doc.querySelectorAll('.btree-paywall-anchor[data-required-access]')).forEach((anchor) => {
        const requiredAccess = anchor.getAttribute('data-required-access');
        const plan = getPaywallPlan(requiredAccess);

        const wrap = doc.createElement('div');
        wrap.className = `btree-paywall-wrap btree-paywall-${plan.code}`;

        const overlay = doc.createElement('div');
        overlay.className = 'btree-paywall-overlay';
        const featureHtml = plan.features.map((feature) => `
          <span class="btree-paywall-chip">
            <span class="btree-paywall-chip-icon">${feature.icon}</span>
            <span>${feature.label}</span>
          </span>
        `).join('');

        overlay.innerHTML = `
          <div class="btree-paywall-panel">
            <div class="btree-paywall-lock">&#128275;</div>
            <div class="btree-paywall-badge">${plan.badge}</div>
            <h2 class="btree-paywall-title">${plan.title}</h2>
            <p class="btree-paywall-copy">${plan.description}</p>
            <div class="btree-paywall-chips">${featureHtml}</div>
            <a class="btree-paywall-cta" href="${getPaywallHref(plan, isAuthenticated)}" target="_parent">${isAuthenticated ? plan.cta : `Sign in to choose ${plan.code === 'premium' ? 'Premium' : 'Basic'}`}</a>
            <div class="btree-paywall-note">${plan.note}</div>
          </div>
        `;

        wrap.appendChild(overlay);

        anchor.replaceWith(wrap);
      });

      return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
    } catch (e) {
      console.error('Failed to build iframe doc:', e);
      return null;
    }
  }, [report, isAuthenticated]);

  return (
    <>
      <PageMeta
        title={report?.title ? `${report.title} | The B Tree` : 'Protected Report | The B Tree'}
        description="Read the free sections of this report without an account. Sign in and subscribe to unlock protected content."
        robots="noindex, nofollow"
      />
      <div className="min-h-screen bg-[#F7F5F2]">
      <div className="w-full max-w-[1480px] mx-auto px-3 sm:px-5 lg:px-7 xl:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Back
            </button>
            <h1 className="text-3xl md:text-4xl font-[Cormorant_Garamond] font-medium text-slate-900 mt-2">
              {report?.title || 'Report'}
            </h1>
            {report?.category && (
              <p className="text-slate-600 mt-1">{report.category}</p>
            )}
            <p className="text-sm text-slate-600 mt-2">{totalPages} pages</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-[#C8A84B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-white border border-red-200 rounded-2xl p-6 text-red-700">
            {error}
          </div>
        ) : pages.length === 0 && !iframeDoc ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 text-slate-700">
            No content available.
          </div>
        ) : iframeDoc ? (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
            <iframe
              ref={iframeRef}
              title={report?.title || 'Report'}
              srcDoc={iframeDoc}
              className="block w-full"
              style={{ height: `${iframeHeight}px`, border: '0', overflow: 'hidden' }}
              sandbox="allow-same-origin allow-top-navigation-by-user-activation"
              scrolling="no"
              onLoad={handleIframeLoad}
            />
          </div>
        ) : (
          <div className="space-y-6">
            {pages.map((p) => {
              const isLocked = p?.locked ?? p?.is_locked;
              const requiredAccess = p?.requiredAccess ?? p?.required_access;
              const pageNumber = p?.pageNumber ?? p?.page_number;
              const content = p?.content || '';

              if (isLocked) {
                const plan = getPaywallPlan(requiredAccess);
                return (
                  <div key={pageNumber} className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
                    <div className="p-6 blur-sm opacity-40">
                      <div className="text-sm text-slate-500 mb-3">Page {pageNumber}</div>
                      <div className="h-72 bg-slate-50 rounded-xl" />
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center bg-[#F7F5F2]/85 backdrop-blur-sm px-5 py-10 text-center">
                      <div className="max-w-3xl">
                        {plan.code === 'basic' ? (
                          <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300 to-orange-500 shadow-lg shadow-orange-500/20 mb-5">
                            <Lock className="w-5 h-5 text-slate-900" />
                          </div>
                        ) : (
                          <div className="inline-flex rounded-full border border-slate-300 bg-white/70 px-5 py-2 text-xs font-bold uppercase tracking-[0.22em] text-slate-900 shadow-sm mb-5">
                            {plan.badge}
                          </div>
                        )}
                        <div className="text-3xl md:text-5xl font-[Cormorant_Garamond] font-medium text-slate-950 mb-4">
                          {plan.title}
                        </div>
                        <div className="text-base md:text-lg leading-8 text-slate-700 mb-6">
                          {plan.description}
                        </div>
                        <a
                          href={getPaywallHref(plan, isAuthenticated)}
                          className={`inline-flex min-h-14 items-center justify-center rounded-xl px-8 text-lg font-extrabold text-white shadow-xl ${
                            plan.code === 'premium'
                              ? 'bg-gradient-to-br from-[#203765] to-[#0f1f3b] shadow-slate-900/20'
                              : 'bg-gradient-to-br from-[#2e60a5] to-[#163e7a] shadow-blue-900/20'
                          }`}
                        >
                          {isAuthenticated
                            ? (plan.code === 'premium' ? 'Unlock Premium - ₹999/month' : 'Unlock Basic - ₹299/month')
                            : `Sign in to choose ${plan.code === 'premium' ? 'Premium' : 'Basic'}`}
                        </a>
                        <div className="mt-4 text-sm text-slate-500">
                          {plan.note}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={pageNumber} className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="text-sm text-slate-500 mb-3">Page {pageNumber}</div>
                  <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: content }} />
                  {requiredAccess && (
                    <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
                      <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600">
                        {getPaywallPlan(requiredAccess).badge}
                      </div>
                      <p className="mt-3 text-lg font-semibold text-slate-900">
                        Sign in and choose a plan to continue reading.
                      </p>
                      <a
                        href={getPaywallHref(getPaywallPlan(requiredAccess), isAuthenticated)}
                        className="mt-5 inline-flex min-h-12 items-center justify-center rounded-xl bg-[#0f1f3b] px-6 font-bold text-white"
                      >
                        {isAuthenticated ? 'View subscription plans' : 'Sign in to choose a plan'}
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default SubscriptionReportViewer;
